// The partner invite token, kept alive past the URL that carried it.
//
// 🚨 THE HOLE THIS CLOSES. A partner refers a buyer; the buyer gets an email with
// ${APP_URL}/register?invite=<token>. Register.js read that token from
// searchParams and nowhere else, so it existed for exactly as long as the tab
// did. A buyer who opened the link, looked around, and came back the next day —
// from a bookmark, the app, or by typing the address — arrived with no token.
// They registered successfully, and the referral stayed 'pending' forever.
//
// ⚠️ THE FAILURE IS SILENT AND INDISTINGUISHABLE FROM NON-CONVERSION. Nothing
// errors. The borrower looks organic, the partner never sees them in their
// portal, and the referral sits in the same state as one the buyer simply
// ignored. In production today 6 referrals are 'pending' with only 3 linked to a
// user, and there is no way to tell which shape each one is.
//
// ⚠️ AND IT IS A PAYMENT PATH NOW. Partner commission and Partner Success rep
// commission both key on the borrower→partner link. A lost token is an unpaid
// commission that nobody can detect, because the evidence it should have existed
// went with the query string.

const KEY = 'doorly_partner_invite';

// ⚠️ AN EXPIRY, BECAUSE A PERSISTED TOKEN IS A SMALL LIABILITY. On a shared
// device a token left indefinitely would attach the NEXT person to register to
// somebody else's partner — the same silent wrongness in the other direction,
// and worse, because it creates a link rather than losing one. 30 days is past
// any plausible click-to-register gap and well short of "forever".
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// ⚠️ EVERY ACCESS IS WRAPPED. Safari private mode throws on localStorage rather
// than returning null, and an exception here would take down the registration
// page — trading a lost referral for a lost customer.
function safeGet() {
  try { return window.localStorage.getItem(KEY); } catch (_) { return null; }
}
function safeSet(v) {
  try { window.localStorage.setItem(KEY, v); } catch (_) { /* nothing to do */ }
}
function safeRemove() {
  try { window.localStorage.removeItem(KEY); } catch (_) { /* nothing to do */ }
}

/** Remember a token seen in the URL. */
export function rememberPartnerInvite(token) {
  if (!token) return;
  safeSet(JSON.stringify({ token, at: Date.now() }));
}

/**
 * The token to register with: the one in the URL if there is one, otherwise the
 * remembered one.
 *
 * ⚠️ THE URL ALWAYS WINS. Someone arriving on a fresh invite link is acting on
 * THAT invite; a stale stored token must never override the one they just
 * clicked. Seeing a URL token also overwrites the stored one, so the two cannot
 * drift apart.
 */
export function resolvePartnerInvite(urlToken) {
  if (urlToken) { rememberPartnerInvite(urlToken); return urlToken; }
  const raw = safeGet();
  if (!raw) return '';
  try {
    const { token, at } = JSON.parse(raw);
    if (!token || !at || Date.now() - at > MAX_AGE_MS) { safeRemove(); return ''; }
    return token;
  } catch (_) {
    // Corrupt or from an older shape — drop it rather than guessing.
    safeRemove();
    return '';
  }
}

/**
 * Forget it. Called after ANY successful registration, not only one that used
 * the token: once this browser has made an account, a token still sitting here
 * can only ever attach the wrong person.
 */
export function clearPartnerInvite() { safeRemove(); }
